import type { AxiosError } from "axios";
import { createContext, useCallback, useEffect, useState } from "react";
import { TbCreditCard, TbShoppingBag, TbUserCircle } from "react-icons/tb";

import logoImg from "~/assets/img/gpaLogo.png";
import { KiwifyCheckoutButton } from "~/components/KiwifyCheckoutButton";
import { PaymentGatewayRenderer } from "~/components/PaymentGatewayRenderer";
import { useQuery } from "~/hooks/useQuery";
import { useRequest } from "~/hooks/useRequest";
import { getPlan } from "~/modules/sales/services";
import type { PaymentPlan } from "~/modules/sales/types";
import { insertToast } from "~/modules/toasts/services";
import { api } from "~/utils/api";
import { CardForm } from "../../../forms/Sales/Checkout/CardForm";
import { CustomerData } from "./components/CustomerData";
import { Frame } from "./components/Frame";
import { LoadingModal } from "./components/LoadingModal";
import { ReceiptAndDiscount } from "./components/ReceiptAndDiscount";
import { RequestModal } from "./components/RequestModal";

type FrameType = "customer" | "card";

type SubscriptionsCheckoutContextData = {
	formData: any;
	plan?: PaymentPlan;
	accumulateFormData(payload: any): void;
	handleSetCurrentFrame(frame: FrameType): void;
};

export const subscriptionsCheckoutContext =
	createContext<SubscriptionsCheckoutContextData>(
		{} as SubscriptionsCheckoutContextData,
	);

export function SubscriptionsCheckout() {
	const [formData, setFormData] = useState<any>();
	const [currentFrame, setCurrentFrame] = useState<FrameType>("customer");
	const [purchageStatus, setPurchaseStatus] = useState({
		executed: false,
		success: false,
	});
	const query = useQuery();
	const {
		data: plan,
		loading,
		error,
	} = useRequest<PaymentPlan | undefined>({
		requestFn: async () => {
			const planId = query.get("plan");
			return planId ? getPlan(planId) : undefined;
		},
	});

	const accumulateFormData = useCallback((payload: any) => {
		setFormData((s: any) => ({ ...s, ...payload }));
	}, []);

	const handleSetCurrentFrame = useCallback((frame: FrameType) => {
		setCurrentFrame(frame);
	}, []);

	const handleCreateSubscription = useCallback(
		async (cardData: any) => {
			return api
				.post("/sales/subscriptions", { ...formData, card: cardData })
				.then(() => {
					setPurchaseStatus({
						success: true,
						executed: true,
					});
				})
				.catch((error: AxiosError<APIError>) => {
					insertToast({
						duration: 5 * 60,
						message: error.response?.data.message,
						title: "Atenção",
						type: "error",
					});
					setPurchaseStatus({
						success: false,
						executed: true,
					});
				});
		},
		[formData],
	);

	useEffect(() => {
		if (plan) {
			setFormData((s: any) => ({ ...s, planId: plan.id }));
		}
	}, [plan, error]);

	return plan ? (
		<>
			<subscriptionsCheckoutContext.Provider
				value={{
					formData,
					plan,
					accumulateFormData,
					handleSetCurrentFrame,
				}}
			>
				<div
					className="flex flex-col items-center gap-5 w-full bg-gpa-gray-50
            pt-8 pb-10 min-h-screen"
				>
					<img src={logoImg} alt="GPA" />
					<div
						className="flex w-full max-h-fit flex-1 flex-col-reverse md:flex-row
              p-5 max-w-6xl mx-auto gap-8 self-start"
					>
						<div className="relative flex flex-col flex-1 gap-4">
							<Frame
								title="Meus dados"
								icon={<TbUserCircle size={24} className="text-gpa-blue-500" />}
								open={currentFrame === "customer"}
								editable={currentFrame !== "customer" && formData?.customerId}
								onEdit={() => setCurrentFrame("customer")}
							>
								<CustomerData />
							</Frame>
							<Frame
								title="Dados do cartão"
								icon={<TbCreditCard size={24} className="text-gpa-blue-500" />}
								open={currentFrame === "card"}
								editable={currentFrame !== "card" && formData?.card}
							>
								<PaymentGatewayRenderer
									pagarmeComponent={
										<CardForm
											onSubmit={handleCreateSubscription}
											paymentPlan={plan!}
										/>
									}
									kiwifyComponent={<KiwifyCheckoutButton planId={plan.id} />}
								/>
							</Frame>
						</div>
						<div
							className="self-start w-full md:sticky md:top-10 flex-1
                md:max-w-[400px]"
						>
							<Frame
								title="Resumo"
								icon={<TbShoppingBag size={24} className="text-gpa-blue-500" />}
								open
							>
								<ReceiptAndDiscount />
							</Frame>
						</div>
					</div>
				</div>
			</subscriptionsCheckoutContext.Provider>
			<LoadingModal loading={loading} />
			<RequestModal
				visible={purchageStatus.executed && purchageStatus.success}
			/>
		</>
	) : null;
}
